import { useEffect, useRef, useState } from 'react'
import { FiBold, FiHash, FiImage, FiLink, FiList, FiTrash2, FiUpload } from 'react-icons/fi'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import {
  createNewWebinar,
  getWebinar,
  uploadWebinarCoverImage,
  updateExistingWebinar,
} from '@domain/features/webinars/webinars-feature'
import { useWebinarCategories } from '@presentation/hooks/useWebinarCategories'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import SeoHead from '@presentation/seo/SeoHead'

interface WebinarDetailEditFormState {
  title: string
  webinarDate: string
  webinarTime: string
  timeZone: string
  durationMinutes: string
  youtubeUrl: string
  coverImageUrl: string
  webinarCategoryId: string
  summary: string
  registerUrl: string
}

function defaultTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

function createEmptyForm(): WebinarDetailEditFormState {
  return {
    title: '',
    webinarDate: '',
    webinarTime: '',
    timeZone: defaultTimeZone(),
    durationMinutes: '',
    youtubeUrl: '',
    coverImageUrl: '',
    webinarCategoryId: '',
    summary: '',
    registerUrl: '',
  }
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

function toTimeInputValue(date: Date): string {
  const hours = `${date.getHours()}`.padStart(2, '0')
  const minutes = `${date.getMinutes()}`.padStart(2, '0')
  return `${hours}:${minutes}`
}

function toFormState(webinar: {
  title: string
  webinarDate: Date
  webinarTime?: string
  timeZone?: string
  durationMinutes?: number
  youtubeUrl?: string
  coverImageUrl?: string
  webinarCategoryId?: string
  summary?: string
  registerUrl?: string
}): WebinarDetailEditFormState {
  return {
    title: webinar.title,
    webinarDate: toDateInputValue(webinar.webinarDate),
    webinarTime: webinar.webinarTime ?? toTimeInputValue(webinar.webinarDate),
    timeZone: webinar.timeZone ?? defaultTimeZone(),
    durationMinutes:
      typeof webinar.durationMinutes === 'number'
        ? `${webinar.durationMinutes}`
        : '',
    youtubeUrl: webinar.youtubeUrl ?? '',
    coverImageUrl: webinar.coverImageUrl ?? '',
    webinarCategoryId: webinar.webinarCategoryId ?? '',
    summary: webinar.summary ?? '',
    registerUrl: webinar.registerUrl ?? '',
  }
}

function parseInputDateTime(dateValue: string, timeValue: string): Date | null {
  const normalizedDate = dateValue.trim()
  const normalizedTime = timeValue.trim()

  if (!normalizedDate || !normalizedTime) {
    return null
  }

  const parsed = new Date(`${normalizedDate}T${normalizedTime}:00`)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return parsed
}

interface SummaryEditResult {
  nextSummary: string
  selectionStart: number
  selectionEnd: number
}

interface PendingSummarySelection {
  start: number
  end: number
  scrollTop: number
}

function wrapSelectionWithMarkdown(
  summary: string,
  selectionStart: number,
  selectionEnd: number,
  prefix: string,
  suffix: string,
  fallbackText: string,
): SummaryEditResult {
  const selectedText = summary.slice(selectionStart, selectionEnd)
  const innerText = selectedText.length > 0 ? selectedText : fallbackText
  const wrapped = `${prefix}${innerText}${suffix}`
  const nextSummary = `${summary.slice(0, selectionStart)}${wrapped}${summary.slice(selectionEnd)}`
  const adjustedStart = selectionStart + prefix.length

  return {
    nextSummary,
    selectionStart: adjustedStart,
    selectionEnd: adjustedStart + innerText.length,
  }
}

function prefixSelectedLinesWithMarkdown(
  summary: string,
  selectionStart: number,
  selectionEnd: number,
  formatLine: (line: string, lineIndex: number) => string,
): SummaryEditResult {
  const lineStartIndex = summary.lastIndexOf('\n', Math.max(0, selectionStart - 1)) + 1
  const lineBreakAfterSelection = summary.indexOf('\n', selectionEnd)
  const lineEndIndex = lineBreakAfterSelection === -1 ? summary.length : lineBreakAfterSelection
  const selectedBlock = summary.slice(lineStartIndex, lineEndIndex)
  const lines = selectedBlock.split('\n')
  const formattedBlock = lines.map((line, index) => formatLine(line, index)).join('\n')
  const nextSummary = `${summary.slice(0, lineStartIndex)}${formattedBlock}${summary.slice(lineEndIndex)}`

  return {
    nextSummary,
    selectionStart: lineStartIndex,
    selectionEnd: lineStartIndex + formattedBlock.length,
  }
}

function insertMarkdownLink(
  summary: string,
  selectionStart: number,
  selectionEnd: number,
): SummaryEditResult {
  const selectedText = summary.slice(selectionStart, selectionEnd)
  const linkLabel = selectedText.length > 0 ? selectedText : 'link text'
  const linkUrl = 'https://example.com'
  const markdownLink = `[${linkLabel}](${linkUrl})`
  const nextSummary = `${summary.slice(0, selectionStart)}${markdownLink}${summary.slice(selectionEnd)}`
  const urlStart = selectionStart + 1 + linkLabel.length + 2

  return {
    nextSummary,
    selectionStart: urlStart,
    selectionEnd: urlStart + linkUrl.length,
  }
}

export default function WebinarDetailEditPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const screenPath = `${location.pathname}${location.search || ''}`
  const { webinarId } = useParams<{ webinarId?: string }>()
  const isEditMode = Boolean(webinarId)
  const { user, loading: authLoading } = useAuthUser()
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useWebinarCategories(true)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const summaryTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const pendingSummarySelectionRef = useRef<PendingSummarySelection | null>(null)

  const [form, setForm] = useState<WebinarDetailEditFormState | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isEditMode) {
      setForm(createEmptyForm())
      setLoading(false)
      setError(null)
      return
    }

    if (!webinarId) {
      setLoading(false)
      setError('Webinar id is missing.')
      return
    }
    const resolvedWebinarId = webinarId

    let cancelled = false

    async function load() {
      try {
        setLoading(true)
        setError(null)
        const webinar = await getWebinar(resolvedWebinarId)
        if (!cancelled) {
          setForm(toFormState(webinar))
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load webinar.',
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [isEditMode, webinarId])

  useEffect(() => {
    if (authLoading) {
      return
    }
    if (!user?.isAdmin) {
      navigate('/webinars', { replace: true })
    }
  }, [authLoading, navigate, user])

  const inputClassName =
    'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors placeholder:text-text-muted focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0!'
  const sectionClassName = 'rounded-2xl border border-border-light bg-surface p-4'
  const summaryToolbarButtonClassName =
    'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background text-text-secondary transition-colors hover:border-blue hover:text-blue disabled:cursor-not-allowed disabled:opacity-60'

  useEffect(() => {
    const pendingSelection = pendingSummarySelectionRef.current
    const textarea = summaryTextareaRef.current
    if (!pendingSelection || !textarea) {
      return
    }

    textarea.focus()
    textarea.setSelectionRange(pendingSelection.start, pendingSelection.end)
    textarea.scrollTop = pendingSelection.scrollTop
    pendingSummarySelectionRef.current = null
  }, [form?.summary])

  const handleInputChange = (
    key: keyof WebinarDetailEditFormState,
    value: string,
  ) => {
    setForm((current) => {
      if (!current) return current
      return { ...current, [key]: value }
    })
  }

  const applySummaryTransform = (
    transform: (
      summary: string,
      selectionStart: number,
      selectionEnd: number,
    ) => SummaryEditResult,
  ) => {
    setForm((current) => {
      if (!current) {
        return current
      }

      const textarea = summaryTextareaRef.current
      const fallbackSelection = current.summary.length
      const selectionStart = textarea?.selectionStart ?? fallbackSelection
      const selectionEnd = textarea?.selectionEnd ?? fallbackSelection
      const scrollTop = textarea?.scrollTop ?? 0
      const result = transform(current.summary, selectionStart, selectionEnd)

      pendingSummarySelectionRef.current = {
        start: result.selectionStart,
        end: result.selectionEnd,
        scrollTop,
      }

      return {
        ...current,
        summary: result.nextSummary,
      }
    })
  }

  const handleSummaryBold = () => {
    applySummaryTransform((summary, selectionStart, selectionEnd) => {
      const selectedText = summary.slice(selectionStart, selectionEnd)
      if (!selectedText) {
        return wrapSelectionWithMarkdown(
          summary,
          selectionStart,
          selectionEnd,
          '**',
          '**',
          'bold text',
        )
      }

      const leadingWhitespaceMatch = selectedText.match(/^\s*/)
      const trailingWhitespaceMatch = selectedText.match(/\s*$/)
      const leadingWhitespace = leadingWhitespaceMatch?.[0] ?? ''
      const trailingWhitespace = trailingWhitespaceMatch?.[0] ?? ''
      const trimmedSelection = selectedText.trim()
      if (!trimmedSelection) {
        return wrapSelectionWithMarkdown(
          summary,
          selectionStart,
          selectionEnd,
          '**',
          '**',
          'bold text',
        )
      }

      const wrappedSelection = `${leadingWhitespace}**${trimmedSelection}**${trailingWhitespace}`
      const nextSummary = `${summary.slice(0, selectionStart)}${wrappedSelection}${summary.slice(selectionEnd)}`
      const nextSelectionStart = selectionStart + leadingWhitespace.length + 2

      return {
        nextSummary,
        selectionStart: nextSelectionStart,
        selectionEnd: nextSelectionStart + trimmedSelection.length,
      }
    })
  }

  const handleSummaryBulletList = () => {
    applySummaryTransform((summary, selectionStart, selectionEnd) =>
      prefixSelectedLinesWithMarkdown(
        summary,
        selectionStart,
        selectionEnd,
        (line) => `- ${line}`,
      ))
  }

  const handleSummaryNumberedList = () => {
    applySummaryTransform((summary, selectionStart, selectionEnd) =>
      prefixSelectedLinesWithMarkdown(
        summary,
        selectionStart,
        selectionEnd,
        (line, lineIndex) => `${lineIndex + 1}. ${line}`,
      ))
  }

  const handleSummaryLink = () => {
    applySummaryTransform(insertMarkdownLink)
  }

  const handleSummaryToolbarMouseDown = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
  }

  const openImagePicker = () => {
    if (saving || isUploadingImage) {
      return
    }
    fileInputRef.current?.click()
  }

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) {
      return
    }

    setIsUploadingImage(true)
    setError(null)
    try {
      const uploadedImageUrl = await uploadWebinarCoverImage(file)
      setForm((current) => {
        if (!current) {
          return current
        }
        return {
          ...current,
          coverImageUrl: uploadedImageUrl,
        }
      })
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : 'Failed to upload webinar cover image.',
      )
    } finally {
      setIsUploadingImage(false)
    }
  }

  const removeImage = () => {
    setForm((current) => {
      if (!current) {
        return current
      }
      return {
        ...current,
        coverImageUrl: '',
      }
    })
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!form) {
      return
    }

    const webinarDate = parseInputDateTime(form.webinarDate, form.webinarTime)
    if (!webinarDate) {
      setError('Valid webinar date and time are required.')
      return
    }

    const normalizedTimeZone = form.timeZone.trim()
    if (normalizedTimeZone.length === 0) {
      setError('Time zone is required.')
      return
    }

    const durationMinutes = Number.parseInt(form.durationMinutes, 10)
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
      setError('Duration must be a positive number of minutes.')
      return
    }

    const normalizedRegisterUrl = form.registerUrl.trim()
    const normalizedYoutubeUrl = form.youtubeUrl.trim()
    const normalizedCoverImageUrl = form.coverImageUrl.trim()
    const normalizedWebinarCategoryId = form.webinarCategoryId.trim()
    const normalizedSummary = form.summary.trim()

    setSaving(true)
    setError(null)

    try {
      if (isEditMode && webinarId) {
        await updateExistingWebinar(webinarId, {
          title: form.title.trim(),
          webinarDate,
          webinarTime: form.webinarTime.trim(),
          timeZone: normalizedTimeZone,
          durationMinutes,
          youtubeUrl: normalizedYoutubeUrl.length > 0 ? normalizedYoutubeUrl : undefined,
          coverImageUrl: normalizedCoverImageUrl.length > 0 ? normalizedCoverImageUrl : '',
          webinarCategoryId:
            normalizedWebinarCategoryId.length > 0 ? normalizedWebinarCategoryId : undefined,
          summary: normalizedSummary.length > 0 ? normalizedSummary : undefined,
          registerUrl: normalizedRegisterUrl,
        })
        AnalyticsFeature.trackWebinarSubmit(screenPath, 'webinar_edit', true)
        navigate(`/webinars/${webinarId}`)
      } else {
        const createdWebinarId = await createNewWebinar({
          title: form.title.trim(),
          webinarDate,
          webinarTime: form.webinarTime.trim(),
          timeZone: normalizedTimeZone,
          durationMinutes,
          youtubeUrl: normalizedYoutubeUrl || undefined,
          coverImageUrl: normalizedCoverImageUrl || undefined,
          webinarCategoryId: normalizedWebinarCategoryId || undefined,
          summary: normalizedSummary || undefined,
          registerUrl: normalizedRegisterUrl || undefined,
        })
        AnalyticsFeature.trackWebinarSubmit(screenPath, 'webinar_create', true)
        navigate(`/webinars/${createdWebinarId}`)
      }
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : 'Failed to save webinar.',
      )
    } finally {
      setSaving(false)
    }
  }

  if (authLoading || loading) {
    return <LoadingSpinner className="min-h-[50vh]" />
  }

  if (error && !form) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <p className="text-lg font-semibold text-text-secondary">{error}</p>
        <Link
          to={isEditMode && webinarId ? `/webinars/${webinarId}` : '/webinars'}
          className="mt-4 text-sm font-semibold text-primary hover:text-primary-light"
        >
          &larr; Back
        </Link>
      </div>
    )
  }

  if (!form || !user?.isAdmin) {
    return <LoadingSpinner className="min-h-[50vh]" />
  }

  const cancelTarget = isEditMode && webinarId ? `/webinars/${webinarId}` : '/webinars'

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <SeoHead
        title={isEditMode ? 'Edit Webinar | Parkinson\'s Insights' : 'Create Webinar | Parkinson\'s Insights'}
        canonicalPath={isEditMode && webinarId ? `/webinars/${webinarId}/edit` : '/webinars/create'}
        noindex
      />
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-text-primary">
          {isEditMode ? 'Edit Webinar' : 'Create Webinar'}
        </h1>
        <Link
          to={cancelTarget}
          className="text-sm font-semibold text-primary hover:text-primary-light"
        >
          Cancel
        </Link>
      </div>

      {error ? (
        <p className="mt-3 rounded-xl bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      ) : null}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />

        <section className={sectionClassName}>
          <label className="block text-sm font-semibold text-text-secondary">
            Title
            <input
              value={form.title}
              onChange={(event) => handleInputChange('title', event.target.value)}
              className={`${inputClassName} mt-2`}
              required
            />
          </label>

          <div className="mt-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-text-secondary">Cover image</p>
              {form.coverImageUrl ? (
                <button
                  type="button"
                  onClick={removeImage}
                  disabled={isUploadingImage || saving}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-500 text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label="Remove cover image"
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            {form.coverImageUrl ? (
              <div className="mt-3">
                <div className="relative overflow-hidden rounded-xl border border-border-light bg-border-light">
                  <img
                    src={form.coverImageUrl}
                    alt="Webinar cover"
                    className="h-56 w-full object-cover"
                  />
                  {isUploadingImage ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/45 text-sm font-semibold text-white">
                      Uploading...
                    </div>
                  ) : null}
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={openImagePicker}
                    disabled={isUploadingImage || saving}
                    className="inline-flex items-center gap-2 rounded-full border border-blue px-4 py-2 text-sm font-semibold text-blue transition-colors hover:bg-blue/10 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FiUpload className="h-4 w-4" />
                    Upload new image
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={openImagePicker}
                disabled={isUploadingImage || saving}
                className="mt-3 flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border px-4 py-8 text-text-secondary transition-colors hover:border-blue hover:text-blue disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FiImage className="h-6 w-6" />
                <span className="text-sm font-semibold">
                  {isUploadingImage ? 'Uploading image...' : 'Add new image'}
                </span>
              </button>
            )}
          </div>

          <label className="mt-4 block text-sm font-semibold text-text-secondary">
            Webinar category
            <select
              value={form.webinarCategoryId}
              onChange={(event) => handleInputChange('webinarCategoryId', event.target.value)}
              className={`${inputClassName} mt-2`}
              disabled={categoriesLoading}
            >
              <option value="">No category</option>
              {categories.map((category) => (
                <option key={category.uid} value={category.uid}>
                  {category.name}
                </option>
              ))}
            </select>
            {categoriesError ? (
              <span className="mt-1 block text-xs font-medium text-red-500">
                Unable to load categories: {categoriesError}
              </span>
            ) : null}
            {!categoriesLoading && !categoriesError && categories.length === 0 ? (
              <span className="mt-1 block text-xs font-medium text-text-muted">
                No active categories are available.
              </span>
            ) : null}
          </label>
        </section>

        <section className={sectionClassName}>
          <label className="block text-sm font-semibold text-text-secondary">
            Webinar date
            <input
              type="date"
              value={form.webinarDate}
              onChange={(event) => handleInputChange('webinarDate', event.target.value)}
              className={`${inputClassName} mt-2`}
              required
            />
          </label>
          <label className="mt-4 block text-sm font-semibold text-text-secondary">
            Time
            <input
              type="time"
              value={form.webinarTime}
              onChange={(event) => handleInputChange('webinarTime', event.target.value)}
              className={`${inputClassName} mt-2`}
              required
            />
          </label>
          <label className="mt-4 block text-sm font-semibold text-text-secondary">
            Time zone
            <input
              value={form.timeZone}
              onChange={(event) => handleInputChange('timeZone', event.target.value)}
              className={`${inputClassName} mt-2`}
              placeholder="Europe/London"
              required
            />
          </label>
          <label className="mt-4 block text-sm font-semibold text-text-secondary">
            Duration (minutes)
            <input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={form.durationMinutes}
              onChange={(event) => handleInputChange('durationMinutes', event.target.value)}
              className={`${inputClassName} mt-2`}
              placeholder="60"
              required
            />
          </label>
        </section>

        <section className={sectionClassName}>
          <label className="block text-sm font-semibold text-text-secondary">
            YouTube URL
            <input
              value={form.youtubeUrl}
              onChange={(event) => handleInputChange('youtubeUrl', event.target.value)}
              className={`${inputClassName} mt-2`}
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </label>

          <label className="mt-4 block text-sm font-semibold text-text-secondary">
            Register URL
            <input
              value={form.registerUrl}
              onChange={(event) => handleInputChange('registerUrl', event.target.value)}
              className={`${inputClassName} mt-2`}
              placeholder="https://..."
            />
          </label>
          <div className="mt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label htmlFor="webinar-summary" className="text-sm font-semibold text-text-secondary">
                Summary
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSummaryBold}
                  onMouseDown={handleSummaryToolbarMouseDown}
                  className={summaryToolbarButtonClassName}
                  disabled={saving || isUploadingImage}
                  aria-label="Make summary text bold"
                >
                  <FiBold className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleSummaryBulletList}
                  onMouseDown={handleSummaryToolbarMouseDown}
                  className={summaryToolbarButtonClassName}
                  disabled={saving || isUploadingImage}
                  aria-label="Add bullet list formatting"
                >
                  <FiList className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleSummaryNumberedList}
                  onMouseDown={handleSummaryToolbarMouseDown}
                  className={summaryToolbarButtonClassName}
                  disabled={saving || isUploadingImage}
                  aria-label="Add numbered list formatting"
                >
                  <FiHash className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleSummaryLink}
                  onMouseDown={handleSummaryToolbarMouseDown}
                  className={summaryToolbarButtonClassName}
                  disabled={saving || isUploadingImage}
                  aria-label="Add markdown link"
                >
                  <FiLink className="h-4 w-4" />
                </button>
              </div>
            </div>
            <textarea
              id="webinar-summary"
              ref={summaryTextareaRef}
              value={form.summary}
              onChange={(event) => handleInputChange('summary', event.target.value)}
              rows={6}
              className={`${inputClassName} mt-2`}
              placeholder="Webinar summary"
            />
            <p className="mt-2 text-xs text-text-muted">
              Supports Markdown: bold, bullet lists, numbered lists, and links.
            </p>
          </div>
        </section>

        <div className="flex justify-center pt-2">
          <button
            type="submit"
            disabled={saving || isUploadingImage}
            className="inline-flex min-w-36 items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-light disabled:opacity-65"
          >
            {saving ? 'Saving...' : isUploadingImage ? 'Uploading image...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  )
}
